// The one list-screen state machine, extracted from the five screens
// that each re-derived it by imitation (threads, matches, messages,
// thread-detail, reveal-requests). Pure and synchronous so the whole
// machine is unit-testable with zero mocks (list-state.test.ts) -- the
// screens just feed it their query's live flags.

// The precedence below is the behaviour the screens shipped before this
// file existed, read off them rather than redesigned:
//
// 1. 'pending' -- first load in flight (full-screen skeleton/spinner).
// 2. 'error'   -- failed with no data. A background refetch (a failed
//    pull-to-refresh) sets TanStack status to 'error' while leaving
//    previously-loaded data in place, so the full-bleed error is gated
//    on the absence of data -- without that guard one failed pull
//    replaced an already-rendered list with the error screen (caught in
//    review of the pull-to-refresh PR).
// 3. 'stale'   -- failed but data is still on screen: the
//    "Couldn't refresh. Showing what's already loaded." notice above an
//    untouched list. Stale outranks empty so one failed refresh never
//    hides the other signal -- the notice renders above the list, whose
//    own empty state still explains the emptiness.
// 4. 'empty'   -- loaded, zero items.
// 5. 'ready'   -- loaded, items on screen.
export type PaginatedListState = 'pending' | 'error' | 'stale' | 'empty' | 'ready';

// Issue #152 AC3: isOffline is folded into the SAME error/stale
// precedence a fetch failure already uses, not a sixth state -- offline
// reads are meant to serve stale data through the existing notice, not
// invent a new one. Optional/defaulted so every pre-#152 call site
// (and every existing test above) is unaffected.
//
// A query fetch never actually fires while offline (TanStack's default
// networkMode: 'online' pauses it instead), so it can never resolve to
// isError on its own -- without this, a first load that starts offline
// would sit in isPending forever, spinning on a request that will never
// be attempted. Folding isOffline in here is what turns that into an
// honest 'error' state (with a "Try again" action that becomes real
// again once back online) instead of an infinite spinner.
export function paginatedListState(input: {
  isPending: boolean;
  isError: boolean;
  isOffline?: boolean;
  hasData: boolean;
  itemCount: number;
}): PaginatedListState {
  const isOffline = input.isOffline ?? false;

  // Offline first load: isPending would otherwise stay true forever
  // (see doc comment above) -- surface it as 'error' now rather than
  // spin on a request that will never fire.
  if (input.isPending && isOffline && !input.hasData) return 'error';
  if (input.isPending) return 'pending';
  if (input.isError && !input.hasData) return 'error';
  if (input.isError || isOffline) return 'stale';
  if (input.itemCount === 0) return 'empty';

  return 'ready';
}

// Decision 4 (issue #563): a short list is not an empty one. When a list
// has SOMETHING in it but fewer than its threshold (3 matches, 5
// threads), a small explainer card renders below it -- the dead-space
// under two cards still needs to say something. Zero items stays the
// empty state's job (it already explains the mechanism and carries the
// first action); the threshold and up needs no explanation at all.
export function isSparseList(itemCount: number, threshold: number): boolean {
  return itemCount > 0 && itemCount < threshold;
}
